// Draft seeding of a single-elim bracket: seed 1 first. Drag (or the arrows) to reorder,
// add / remove teams, randomize. Every change is saved (PUT /seeding rebuilds the bracket),
// so the preview next to it always shows the real round 1 and who gets a bye.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';

const nextPow2 = (n) => { let p = 1; while (p < n) p *= 2; return p; };

export default function SeedingEditor({ bracket, allTeams, onChanged }) {
  const { t } = useTranslation();
  const [seeds, setSeeds] = useState(() => (bracket.seeding || []).map(Number));
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [add, setAdd] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSeeds((bracket.seeding || []).map(Number)); }, [bracket.seeding]);

  const byId = Object.fromEntries((allTeams || []).map(tm => [tm.id, tm]));
  const byes = seeds.length >= 2 ? nextPow2(seeds.length) - seeds.length : 0;

  const save = async (next) => {
    const prev = seeds;
    setSeeds(next); setErr(''); setBusy(true);
    try {
      await axios.put(`/api/brackets/${bracket.id}/seeding`, { team_ids: next });
      await onChanged();
    } catch (e) {
      setSeeds(prev);
      setErr((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { setBusy(false); }
  };
  const move = (from, to) => {
    if (to < 0 || to >= seeds.length || from === to) return;
    const next = [...seeds];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    save(next);
  };
  const randomize = async () => {
    setErr(''); setBusy(true);
    try { await axios.post(`/api/brackets/${bracket.id}/randomize`); await onChanged(); }
    catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    finally { setBusy(false); }
  };

  const available = (allTeams || []).filter(tm => !seeds.includes(tm.id)).sort((a, b) => String(a.name).localeCompare(String(b.name)));

  return (
    <div className="br-card">
      <h3>{t('bracket.seeding.title', 'Seeding')}</h3>
      <div className="br-hint" style={{ marginBottom: '0.6rem' }}>
        {t('bracket.seeding.hint', 'Seed 1 is the strongest team. Top seeds meet as late as possible.')}
        {byes > 0 && <> {t('bracket.seeding.byes', 'Seeds 1-{{n}} get a bye in round 1.', { n: byes })}</>}
      </div>
      <div className="br-seeds">
        {seeds.map((id, i) => {
          const tm = byId[id] || { id, name: `#${id}` };
          return (
            <div key={id} draggable={!busy}
              className={`br-seed${overIdx === i ? ' over' : ''}${dragIdx === i ? ' dragging' : ''}`}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
              onDragLeave={() => setOverIdx(null)}
              onDrop={(e) => { e.preventDefault(); if (dragIdx != null) move(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}>
              <span className="no">{i + 1}</span>
              <TeamLogo team={tm} size={20} radius={5} />
              <span className="nm" title={tm.name}>{tm.name}</span>
              {i < byes && <span className="bye">{t('bracket.bye', 'BYE')}</span>}
              <button className="ic" disabled={busy || i === 0} onClick={() => move(i, i - 1)} aria-label={t('bracket.seeding.up', 'Move up')}>▲</button>
              <button className="ic" disabled={busy || i === seeds.length - 1} onClick={() => move(i, i + 1)} aria-label={t('bracket.seeding.down', 'Move down')}>▼</button>
              <button className="ic x" disabled={busy || seeds.length <= 2} onClick={() => save(seeds.filter(x => x !== id))}
                title={seeds.length <= 2 ? t('bracket.seeding.minTwo', 'A bracket needs at least 2 teams') : t('bracket.seeding.remove', 'Remove')}>×</button>
            </div>
          );
        })}
      </div>
      <div className="br-row" style={{ marginTop: '0.6rem' }}>
        <select className="br-select full" style={{ flex: 1 }} value={add} onChange={e => setAdd(e.target.value)} disabled={busy}>
          <option value="">{t('bracket.seeding.addTeam', 'Add a team…')}</option>
          {available.map(tm => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
        </select>
        <button className="br-btn sm" disabled={busy || !add} onClick={() => { save([...seeds, Number(add)]); setAdd(''); }}>{t('bracket.seeding.add', 'Add')}</button>
      </div>
      <div className="br-row" style={{ marginTop: '0.5rem' }}>
        <button className="br-btn ghost sm" disabled={busy || seeds.length < 2} onClick={randomize}>🎲 {t('bracket.seeding.randomize', 'Randomize')}</button>
      </div>
      {err && <div className="br-err">{err}</div>}
    </div>
  );
}
